import React, { createContext, useContext, useState, useEffect } from 'react';

const DealsContext = createContext();

export const useDeals = () => {
  const context = useContext(DealsContext);
  if (!context) {
    throw new Error('useDeals must be used within a DealsProvider');
  }
  return context;
};

const initialDeals = [
  {
    id: 1,
    company: 'Acme Corp',
    contact: 'John Smith',
    value: 50000,
    stage: 'Qualification',
    probability: 25,
    qualityScore: 7.2,
    lastActivity: '2024-01-15',
    notes: 'Strong technical fit, budget confirmed',
    purchaserName: 'Jane Doe',
    purchaserContact: 'jane@acme.com',
    purchaserContactType: 'email',
    purchaserName2: 'Robert Jones',
    purchaserContact2: 'robert@acme.com',
    purchaserContactType2: 'email',
    projectedCloseDate: '2024-03-15'
  },
  {
    id: 2,
    company: 'Tech Solutions Inc',
    contact: 'Sarah Johnson',
    value: 125000,
    stage: 'Demo/Proposal',
    probability: 60,
    qualityScore: 8.5,
    lastActivity: '2024-01-14',
    notes: 'Decision maker engaged, timeline established',
    purchaserName: 'Sarah Johnson',
    purchaserContact: '555-123-4567',
    purchaserContactType: 'phone',
    purchaserName2: 'Michael Thomas',
    purchaserContact2: 'accounts@techsolutions.com',
    purchaserContactType2: 'email',
    projectedCloseDate: '2024-02-28'
  },
  {
    id: 3,
    company: 'Global Industries',
    contact: 'Mike Chen',
    value: 75000,
    stage: 'Negotiation',
    probability: 80,
    qualityScore: 9.1,
    lastActivity: '2024-01-13',
    notes: 'Contract review in progress',
    purchaserName: 'Mike Chen',
    purchaserContact: 'mike.chen@globalind.com',
    purchaserContactType: 'email',
    purchaserName2: 'Lisa Wong',
    purchaserContact2: '555-987-6543',
    purchaserContactType2: 'phone',
    projectedCloseDate: '2024-02-01'
  },
  {
    id: 4,
    company: 'StartupXYZ',
    contact: 'Emily Davis',
    value: 25000,
    stage: 'Discovery',
    probability: 15,
    qualityScore: 5.8,
    lastActivity: '2024-01-12',
    notes: 'Early stage, need to qualify budget',
    purchaserName: '',
    purchaserContact: '',
    purchaserContactType: 'email',
    purchaserName2: '',
    purchaserContact2: '',
    purchaserContactType2: 'email',
    projectedCloseDate: ''
  },
  {
    id: 5,
    company: 'Enterprise Co',
    contact: 'Robert Wilson',
    value: 200000,
    stage: 'Demo/Proposal',
    probability: 70,
    qualityScore: 8.9,
    lastActivity: '2024-01-11',
    notes: 'Executive sponsor identified',
    purchaserName: 'Robert Wilson',
    purchaserContact: 'rwilson@enterprise.co',
    purchaserContactType: 'email',
    purchaserName2: 'Amanda Pierce',
    purchaserContact2: '555-222-3333',
    purchaserContactType2: 'phone',
    projectedCloseDate: '2024-03-01'
  }
];

export const DealsProvider = ({ children }) => {
  const [deals, setDeals] = useState(() => {
    const saved = localStorage.getItem('deals');
    return saved ? JSON.parse(saved) : initialDeals;
  });

  useEffect(() => {
    localStorage.setItem('deals', JSON.stringify(deals));
  }, [deals]);

  const addDeal = (deal) => {
    const newDeal = {
      ...deal,
      id: Date.now(),
      lastActivity: new Date().toISOString().split('T')[0]
    };
    setDeals(prev => [...prev, newDeal]);
  };

  const updateDeal = (id, updates) => {
    setDeals(prev =>
      prev.map(deal =>
        deal.id === id
          ? { ...deal, ...updates, lastActivity: new Date().toISOString().split('T')[0] }
          : deal
      )
    );
  };

  const deleteDeal = (id) => {
    setDeals(prev => prev.filter(deal => deal.id !== id));
  };

  const getQualityMetrics = () => {
    const totalDeals = deals.length;
    const avgQuality = deals.length > 0 ? deals.reduce((sum, deal) => sum + deal.qualityScore, 0) / totalDeals : 0;
    const highQuality = deals.filter(deal => deal.qualityScore >= 8).length;
    const totalValue = deals.reduce((sum, deal) => sum + deal.value, 0);

    // New metrics for deal strength
    const withPurchaserContact = deals.filter(
      deal => deal?.purchaserName && deal?.purchaserContact
    ).length;
    const withSecondaryContact = deals.filter(
      deal => deal?.purchaserName2 && deal?.purchaserContact2
    ).length;
    const withCloseDate = deals.filter(
      deal => deal?.projectedCloseDate
    ).length;
    const strongDeals = deals.filter(
      deal => deal?.purchaserName && deal?.purchaserContact && deal?.projectedCloseDate
    ).length;

    return {
      totalDeals,
      avgQuality: avgQuality.toFixed(1),
      highQuality,
      totalValue,
      withPurchaserContact,
      withSecondaryContact,
      withCloseDate,
      strongDeals
    };
  };

  return (
    <DealsContext.Provider
      value={{
        deals,
        addDeal,
        updateDeal,
        deleteDeal,
        getQualityMetrics
      }}
    >
      {children}
    </DealsContext.Provider>
  );
};